import { cn } from "@/lib/utils";
import mascotSad from "@/assets/mascot-sad.png";
import mascotNormal from "@/assets/mascot-normal.png";
import mascotHappy from "@/assets/mascot-happy.png";

interface FocusMascotScaleProps {
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const focusLevels = [
  { value: 1, label: "too noisy", description: "Constantly distracted" },
  { value: 2, label: "distracting", description: "Hard to concentrate" },
  { value: 3, label: "decent", description: "Got some work done" },
  { value: 4, label: "focused", description: "Productive session" },
  { value: 5, label: "locked in", description: "In the zone" },
];

// Map focus values to mascot images
const getMascotImage = (value: number) => {
  if (value <= 2) return mascotSad;
  if (value <= 3) return mascotNormal;
  return mascotHappy;
};

export function FocusMascotScale({ value, onChange, disabled = false }: FocusMascotScaleProps) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-1">
        {focusLevels.map((level) => (
          <button
            key={level.value}
            type="button"
            onClick={() => onChange(level.value)}
            disabled={disabled}
            className={cn(
              "flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200",
              "border-2",
              value === level.value
                ? "border-primary bg-primary/10 scale-105"
                : "border-transparent bg-secondary/30 hover:bg-secondary/50",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <img
              src={getMascotImage(level.value)}
              alt={level.label}
              className={cn(
                "w-8 h-8 sm:w-10 sm:h-10 object-contain transition-transform",
                value === level.value && "scale-110"
              )}
            />
            <span className="text-lg font-bold">{level.value}</span>
            <span className="text-[9px] text-muted-foreground leading-tight text-center">
              {level.label}
            </span>
          </button>
        ))}
      </div>
      
      {/* Label showing current selection */}
      <div className="text-center">
        {value ? (
          <p className="text-sm">
            <span className="font-medium">{focusLevels[value - 1].label}</span>
            <span className="text-muted-foreground"> — {focusLevels[value - 1].description}</span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Tap to rate your focus level</p>
        )}
      </div>
    </div>
  );
}
